"use client";

import { useLocalStorage, writeStorage } from "@rehooks/local-storage";
import { jwtDecode, type JwtPayload } from "jwt-decode";
import { useEffect } from "react";
import useLocation from "~/bridge/ui/useLocation";
import useSearchParams from "~/bridge/ui/useSearchParams";
import { logout } from "~/use-cases/user/logout";

export function useAuth() {
  const [token] = useLocalStorage<string>("jwt", "");
  const [userData] = useLocalStorage<
    Partial<{
      address: Partial<{
        address_type: string | "home";
        country: string | "NO";
        formatted: string;
        postal_code: string;
        region: string;
        street_address: string;
      }>;
      email: string;
      email_verified: boolean;
      family_name: string;
      firstname: string;
      lastname: string;
      given_name: string;
      name: string;
      other_addresses: any[];
      phone_number: string;
      sid: string;
      sub: string;
    }>
  >("userData", {});

  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Note that we are on the frontend here, and this token is just here to hold some non sensitive data.
    // Service API has a Cookie (http only and Safe only) that is used to hold a really token.
    if (searchParams) {
      const urlToken = searchParams.get("token");
      if (urlToken !== null) {
        //need to do this because nextjs search params are readonly
        let url = new URL(window.location.href);
        let urlSearchParams = new URLSearchParams(url.search);
        urlSearchParams.delete("token");
        try {
          const decoded = jwtDecode<JwtPayload>(urlToken);
          if (decoded) {
            if (decoded.sub === "isSupposedToBeLoggedInOnServiceApi") {
              writeStorage("jwt", urlToken);
            }
          }
        } catch (exception) {
          console.error(exception);
        }
      }

      const userDataParam = searchParams.get("userData");
      if (userDataParam !== null) {
        try {
          const userData = JSON.parse(userDataParam);

          writeStorage("userData", JSON.stringify(userData));
        } catch (exception) {
          console.error(exception);
        }
      }

      if (urlToken !== null || userDataParam !== null) {
        // force reload here as we're playing with cookies.
        window.location.href = location.pathname;
      }
    }
  });

  let isAuthenticated = false;
  let userInfos = userData;
  try {
    const decoded = jwtDecode<any>(token);
    if (decoded && decoded.exp > Date.now() / 1000) {
      isAuthenticated = true;
      userInfos = { ...userInfos, ...decoded };
    }
  } catch (exception) {}

  return {
    login: (jwt: string) => {
      writeStorage("jwt", jwt);
    },
    logout: async () => {
      await logout();
      writeStorage("jwt", "");
    },
    isAuthenticated,
    userInfos,
  };
}
